// src/App.tsx
// This is the main application component that handles routing and workflow steps.
import { useState, useEffect } from 'react';
import { logger } from './utils/logger';
import { trackPageView, trackEvent } from './utils/analytics';
import Navbar from './components/Navbar';
import ModernLandingPage from './components/ModernLandingPage';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import CinematicBackground from './components/CinematicBackground';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { WelcomeSaleProvider } from './contexts/WelcomeSaleContext';
import AuthErrorToast from './components/AuthErrorToast';
import WhatsAppWidget from './components/WhatsAppWidget';
import WelcomeSaleBanner from './components/WelcomeSaleBanner';
import Msg91Chat from './components/Msg91Chat';
import MobileAppPrompt from './components/MobileAppPrompt';
import { 
  Features, 
  HowItWorks, 
  HelpCenter, 
  ContactUs, 
  PrivacyPolicy, 
  TermsOfService,
  AboutUs,
  SupportProject,
  Blog,
  Community
} from './components/pages';
import RefundPolicy from './components/pages/RefundPolicy';
import ShippingPolicy from './components/pages/ShippingPolicy';
import Pricing from './components/pages/Pricing';
import TopPromoStrips from './components/TopPromoStrips';
import { motion, AnimatePresence } from 'framer-motion';

export default function App(): JSX.Element {
  const [currentPage, setCurrentPage] = useState<string>('home');
  const [showMobilePrompt, setShowMobilePrompt] = useState<boolean>(false);

  // Check if user is on Android mobile device and not dismissed in last 7 days
  useEffect(() => {
    const ua = navigator.userAgent;
    const isAndroid = /Android/i.test(ua) && !/wv/.test(ua);
    const isMobile = window.innerWidth <= 768;

    if (isAndroid && isMobile) {
      try {
        const until = localStorage.getItem('nc_mobile_prompt_dismissed_until');
        if (until && Date.now() < Number(until)) {
          return; // Still dismissed
        }
      } catch (e) {
        // ignore storage errors
      }

      // Small delay for better UX
      const timer = setTimeout(() => {
        setShowMobilePrompt(true);
        trackEvent('mobile_app_prompt_shown', {
          user_agent: navigator.userAgent,
          screen_width: window.innerWidth,
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClosePrompt = () => {
    trackEvent('mobile_app_prompt_dismissed', { action: 'close', source: 'app_root' });
    setShowMobilePrompt(false);
    saveDismiss();
  };

  const handleContinueWeb = () => {
    trackEvent('mobile_app_prompt_dismissed', { action: 'continue_web', source: 'app_root' });
    setShowMobilePrompt(false);
    saveDismiss();
  };

  const saveDismiss = () => {
    try {
      const until = Date.now() + 7 * 24 * 60 * 60 * 1000; // 7 days
      localStorage.setItem('nc_mobile_prompt_dismissed_until', String(until));
    } catch (e) {
      // ignore storage errors
    }
  };

  // Track session start on mount
  useEffect(() => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const screenSize = `${window.innerWidth}x${window.innerHeight}`;
    
    trackEvent('session_start', {
      event_category: 'Session',
      device_type: isMobile ? 'mobile' : 'desktop',
      screen_size: screenSize,
      viewport_width: window.innerWidth,
    });
  }, []);

  // Track page views when route changes
  useEffect(() => {
    const getPageTitle = (page: string): string => {
      const titles: Record<string, string> = {
        'home': 'Home - PDF Enhancer',
        'features': 'Features',
        'how-to-print-online-lecture-notes': 'How to Print Online Lecture Notes',
        'help-center': 'Help Center',
        'community': 'Community',
        'contact-us': 'Contact Us',
        'privacy-policy': 'Privacy Policy',
        'refund-policy': 'Refund Policy',
        'terms-of-service': 'Terms of Service',
        'shipping-policy': 'Shipping Policy',
        'about-us': 'About Us',
        'support-project': 'Support NotesCrafter',
        'pricing': 'Community Pass - Pricing',
        'blog': 'Blog',
      };
      return titles[page] || 'NotesCrafter';
    };

    trackPageView(`/${currentPage === 'home' ? '' : currentPage}`, getPageTitle(currentPage));
  }, [currentPage]);

  // Simple page routing based on URL hash or pathname
  useEffect(() => {
    const handleRouteChange = () => {
      const path = window.location.pathname;
      const hash = window.location.hash.replace('#', '');
      
      // Check if it's a page route
      if (path.includes('/admin')) setCurrentPage('admin');
      else if (path.includes('/features')) setCurrentPage('features');
      else if (path.includes('/how-to-print-online-lecture-notes')) setCurrentPage('how-to-print-online-lecture-notes');
      else if (path.includes('/help-center')) setCurrentPage('help-center');
      else if (path.includes('/community')) setCurrentPage('community');
      else if (path.includes('/contact-us')) setCurrentPage('contact-us');
      else if (path.includes('/privacy-policy')) setCurrentPage('privacy-policy');
      else if (path.includes('/refund-policy')) setCurrentPage('refund-policy');
      else if (path.includes('/terms-of-service')) setCurrentPage('terms-of-service');
      else if (path.includes('/shipping-policy')) setCurrentPage('shipping-policy');
      else if (path.includes('/about-us')) setCurrentPage('about-us');
      else if (path.includes('/support-project')) setCurrentPage('support-project');
      else if (path.includes('/pricing')) setCurrentPage('pricing');
      else if (path.includes('/blog')) setCurrentPage('blog');
      else if (hash === 'admin') setCurrentPage('admin');
      else if (hash === 'features') setCurrentPage('features');
      else if (hash === 'how-to-print-online-lecture-notes') setCurrentPage('how-to-print-online-lecture-notes');
      else if (hash === 'community') setCurrentPage('community');
      else if (hash === 'pricing') setCurrentPage('pricing');
      else if (hash === 'blog') setCurrentPage('blog');
      else {
        setCurrentPage('home');
      }
    };

    // Initial route check
    handleRouteChange();

    // Listen for popstate events (back/forward browser navigation)
    window.addEventListener('popstate', handleRouteChange);
    
    // Listen for hash changes
    window.addEventListener('hashchange', handleRouteChange);

    // Listen for custom navigation events
    const handleCustomNavigation = (event: Event) => {
      const customEvent = event as CustomEvent;
      const page = customEvent.detail.page;
      const startProcessing = customEvent.detail.startProcessing;
      
      // If this is a start processing request, redirect to the standalone Flow app
      if (startProcessing) {
        window.open('https://flow.notescrafter.com', '_blank');
        return;
      }

      // Handle regular page navigation
      if (page) {
        setCurrentPage(page);
        
        // Update URL without page reload
        if (page !== 'home') {
          window.history.pushState({}, '', `/${page}`);
        } else {
          window.history.pushState({}, '', '/');
        }
      }

      // Ensure the newly-opened page starts at the top of the document instantly.
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      } catch (e) {
        // Older browsers
        window.scrollTo(0, 0);
      }
    };

    window.addEventListener('navigateToPage', handleCustomNavigation as EventListener);
    window.addEventListener('navigate', handleCustomNavigation as EventListener);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('hashchange', handleRouteChange);
      window.removeEventListener('navigateToPage', handleCustomNavigation as EventListener);
      window.removeEventListener('navigate', handleCustomNavigation as EventListener);
    };
  }, []);

  // Function to navigate to pages
  const navigateToPage = (page: string) => {
    setCurrentPage(page);
    if (page !== 'home') {
      window.history.pushState({}, '', `/${page}`);
    } else {
      window.history.pushState({}, '', '/');
    }
  };

  // Ensure navigateToPage also scrolls to top instantly (used by Footer prop)
  const navigateToPageAndScroll = (page: string) => {
    navigateToPage(page);
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } catch (e) {
      window.scrollTo(0, 0);
    }
  };

  // Initialize performance monitoring (with guard to prevent duplicates)
  useEffect(() => {
    // 🚀 FIX: Guard against duplicate initialization
    if ((window as any).__perfInitialized) {
      return;
    }

    try {
      logger.debug('Performance monitoring initialized');
      (window as any).__perfInitialized = true;
    } catch (error) {
      logger.warn('Failed to initialize performance monitoring:', error);
    }
  }, []);


  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
       <WelcomeSaleProvider>
        <CinematicBackground>
        <div className="min-h-screen flex flex-col overflow-x-hidden w-full max-w-full flex-nowrap">
          {showMobilePrompt && (
            <MobileAppPrompt
              onClose={handleClosePrompt}
              onContinueWeb={handleContinueWeb}
            />
          )}

          <TopPromoStrips />
          <Navbar />
          
          {/* Page Routing - Show page components if not on home */}
          {currentPage !== 'home' && (
          <main className="flex-1 overflow-x-hidden w-full max-w-full">
            <AnimatePresence mode="sync">
              <motion.div
                key={currentPage}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {currentPage === 'features' && <Features />}
                {currentPage === 'how-to-print-online-lecture-notes' && <HowItWorks />}
                {currentPage === 'help-center' && <HelpCenter />}
                {currentPage === 'community' && <Community onNavigate={navigateToPageAndScroll} />}
                {currentPage === 'contact-us' && <ContactUs />}
                {currentPage === 'privacy-policy' && <PrivacyPolicy />}
                {currentPage === 'refund-policy' && <RefundPolicy />}
                {currentPage === 'terms-of-service' && <TermsOfService />}
                {currentPage === 'shipping-policy' && <ShippingPolicy />}
                {currentPage === 'about-us' && <AboutUs />}
                {currentPage === 'support-project' && <SupportProject />}
                {currentPage === 'pricing' && <Pricing />}
                {currentPage === 'blog' && <Blog />}
              </motion.div>
            </AnimatePresence>
          </main>
        )}

  {/* Main App Workflow - Show only when on home page */}
  {currentPage === 'home' && (
          <div className="text-text-main flex-1 w-full max-w-full">
            <main className="w-full pt-16 overflow-x-hidden max-w-full">
              <ModernLandingPage isStandalone={false} />
            </main>
          </div>
      )}

      {/* Footer - Fixed at bottom (hidden on pricing page) */}
      {currentPage !== 'pricing' && (
        <div className="mt-auto">
          <Footer onNavigate={navigateToPageAndScroll} />
        </div>
      )}
        </div>
        <WhatsAppWidget />
        <WelcomeSaleBanner />
        <Msg91Chat />
      </CinematicBackground>
      
      {/* Auth Error Toast - Shows authentication errors */}
      <AuthErrorToast />
      </WelcomeSaleProvider>
      </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}